import {useMemo,useRef,useState} from 'react';
import type {OwnerConnectionState,OwnerSellabilityCommandInput,OwnerSellabilityCommandResult,OwnerSellabilityItem} from './product-types';

function grainLabel(value:OwnerSellabilityItem['grain']){
  return value==='PRODUCT'?'商品':value==='OPTION'?'選項':value==='MODIFIER'?'配料':'套餐子項';
}
function scopeLabel(value:OwnerSellabilityItem['scope']){return value==='ONLINE_ONLY'?'只停網上':'全渠道';}

const FINAL_PRODUCT_MEDIA:Readonly<Record<string,string>>=Object.freeze({
  '紫米飯團 A':'/brand/owner-final/sellability/rice-roll-a.webp',
  '紫米飯團 B':'/brand/owner-final/sellability/rice-roll-b.webp',
  '紫米飯團 C':'/brand/owner-final/sellability/rice-roll-c.webp',
  '紫米飯團 D':'/brand/owner-final/sellability/rice-roll-d.webp',
  '台式炸雞便當':'/brand/owner-final/sellability/fried-chicken-bento.webp',
});
function productMedia(name:string){return FINAL_PRODUCT_MEDIA[name.trim()]??null;}

export function SellabilityPage({
  items,connection,busy,result,onDismissResult,onCommand,onReload,onBack,
}:{
  items:readonly OwnerSellabilityItem[];
  connection:OwnerConnectionState;
  busy:boolean;
  result:OwnerSellabilityCommandResult|null;
  onDismissResult:()=>void;
  onCommand:(input:OwnerSellabilityCommandInput)=>void;
  onReload:()=>void;
  onBack:()=>void;
}){
  const [query,setQuery]=useState('');
  const [view,setView]=useState<'SOLD_OUT'|'ALL'>('ALL');
  const [grain,setGrain]=useState<'ALL'|OwnerSellabilityItem['grain']>('ALL');
  const [scope,setScope]=useState<'ALL'|'ONLINE_ONLY'>('ALL');
  const [temporary,setTemporary]=useState<'NONE'|'TODAY'|'TIME'>('NONE');
  const [restoreAt,setRestoreAt]=useState('');
  const [pending,setPending]=useState<{name:string;input:Omit<OwnerSellabilityCommandInput,'operationId'>}|null>(null);
  const pendingRef=useRef<typeof pending>(null);
  const [confirmationError,setConfirmationError]=useState('');
  const rows=useMemo(()=>items.filter(item=>
    (view==='ALL'||item.state==='SOLD_OUT')&&(grain==='ALL'||item.grain===grain)&&(!query.trim()||item.name.toLowerCase().includes(query.trim().toLowerCase())||item.targetId.toLowerCase().includes(query.trim().toLowerCase()))
  ),[items,grain,query,view]);
  const disabled=busy||connection==='OFFLINE_READONLY'||connection==='PERMISSION_DENIED';

  const command=(item:OwnerSellabilityItem,action:'SOLD_OUT'|'RESTORE')=>{
    if(disabled)return;
    setConfirmationError('');
    if(action==='SOLD_OUT'&&temporary==='TIME'&&(!restoreAt||!Number.isFinite(new Date(restoreAt).getTime()))){
      setConfirmationError('請先填寫有效的恢復時間。');return;
    }
    const until=action==='SOLD_OUT'
      ?temporary==='TODAY'?'TODAY':temporary==='TIME'&&restoreAt?new Date(restoreAt).toISOString():undefined
      :undefined;
    const next={name:item.name,input:{
      action,
      scope,
      targets:[{targetId:item.targetId,grain:item.grain}],
      ...(until?{restoreAt:until}:{}),
      reason:'OWNER_OA_SEL_001',
    }};
    pendingRef.current=next;setPending(next);
  };
  const dismiss=()=>{pendingRef.current=null;setPending(null);};
  const confirm=()=>{
    if(disabled||!pending||pendingRef.current!==pending)return;
    pendingRef.current=null;setPending(null);
    onCommand({...pending.input,operationId:crypto.randomUUID()});
  };

  return <section className="page sellability-page">
    <div className="v3-segments owner-sellability-tabs" role="group" aria-label="供應篩選"><button aria-pressed={view==='SOLD_OUT'} onClick={()=>setView('SOLD_OUT')}>售罄 {items.filter(item=>item.state==='SOLD_OUT').length}</button><button aria-pressed={view==='ALL'} onClick={()=>setView('ALL')}>全部商品</button></div>

    {result?<section className={'sellability-result-panel '+result.state.toLowerCase()} role="status">
      <div className="sellability-result-icon" aria-hidden="true">{result.state==='CONFIRMED'?'✓':result.state==='PARTIAL'?'!':'?'}</div>
      <div><span>更新結果</span><h2>{result.state==='CONFIRMED'?'產品狀態已更新':result.state==='PARTIAL'?'部分產品已更新':'結果待確認'}</h2><p>{result.state==='CONFIRMED'?'最新供應狀態已完成確認。':result.state==='PARTIAL'?'部分項目仍需重新確認。':'暫時唔好重複操作，請先重新讀取狀態。'}</p></div>
      <button onClick={onDismissResult}>收起</button>
    </section>:null}

    <section className="card sellability-controls"><label className="owner-sellability-search"><span>搜尋</span><input value={query} onChange={e=>setQuery(e.target.value)} placeholder="商品名稱"/></label><details><summary>範圍與時限</summary>
      <label><span>類型</span><select value={grain} onChange={e=>setGrain(e.target.value as typeof grain)}>
        <option value="ALL">全部類型</option><option value="PRODUCT">商品</option><option value="OPTION">選項</option><option value="MODIFIER">配料</option><option value="COMBO_CHILD">套餐子項</option>
      </select></label>
      <label><span>停售範圍</span><select value={scope} onChange={e=>setScope(e.target.value as typeof scope)}>
        <option value="ALL">全渠道</option><option value="ONLINE_ONLY">只停網上</option>
      </select></label>
      <label><span>停售時限</span><select value={temporary} onChange={e=>setTemporary(e.target.value as typeof temporary)}>
        <option value="NONE">直至手動恢復</option><option value="TODAY">只停至今日</option><option value="TIME">停至指定時間</option>
      </select></label>
      {temporary==='TIME'?<label><span>恢復時間</span><input type="datetime-local" value={restoreAt} onChange={e=>setRestoreAt(e.target.value)}/></label>:null}
      </details><button className="owner-reload-supply" type="button" onClick={onReload}>重新整理</button>
    </section>

    {confirmationError?<p role="alert">{confirmationError}</p>:null}
    {!rows.length?<section className="card empty-state"><h2>未有符合條件嘅商品</h2><p>可以調整搜尋或類型篩選再查看。</p></section>:
      <div className="sellability-list">{rows.map(item=>{const media=productMedia(item.name);return <article className="card sellability-row" key={item.grain+':'+item.targetId}>
        <div className="sellability-media" data-grain={item.grain}>{media?<img src={media} alt="" loading="lazy" decoding="async"/>:<span aria-hidden="true">{grainLabel(item.grain).slice(0,1)}</span>}</div>
        <div className="sellability-copy">
          <strong>{item.name}</strong>
          <small>{grainLabel(item.grain)} · {scopeLabel(item.scope)} · <span className={'sellability-pos '+(item.posState??'').toLowerCase()}>{item.posLabel??(item.readback==='CONFIRMED'?'已確認':item.readback==='PARTIAL'?'部分確認':'待確認')}</span></small>
          {item.posState==='PENDING'&&item.pendingAction?<small>POS 確認中</small>:null}
          {item.restoreAt?<small>臨時停售至：{new Date(item.restoreAt).toLocaleString('zh-HK')}</small>:null}
          {item.quantity!==undefined?<small>數量 {item.quantity}</small>:null}
        </div>
        <div className="sellability-action">
          <span className={'sellability-state '+item.state.toLowerCase()}>{item.state==='SELLABLE'?'可售':item.state==='SOLD_OUT'?'售罄':'未明'}</span>
          {item.state==='SOLD_OUT'
            ?<button disabled={disabled} onClick={()=>command(item,'RESTORE')}>恢復</button>
            :<button disabled={disabled||item.state==='UNKNOWN'} onClick={()=>command(item,'SOLD_OUT')}>售罄</button>}
        </div>
      </article>})}</div>}

    {pending?<div className="overlay" onKeyDown={event=>{if(event.key==='Escape'){event.preventDefault();event.stopPropagation();dismiss();}}}>
      <section className="sheet sellability-confirm" role="dialog" aria-modal="true" aria-labelledby="sellability-confirm-title">
        <header><div><h2 id="sellability-confirm-title">{pending.input.action==='SOLD_OUT'?'確認停售':'確認恢復供應'}</h2></div></header>
        <p><strong>{pending.name}</strong></p>
        <p>範圍：{scopeLabel(pending.input.scope)}</p>
        <p>{pending.input.action==='SOLD_OUT'?'確認後會停售，所選範圍將無法再點選此項目。':'確認後會恢復供應，所選範圍將可以再次點選此項目。'}</p>
        {pending.input.action==='SOLD_OUT'?<p>恢復時間：{pending.input.restoreAt==='TODAY'?'今日結束':pending.input.restoreAt?new Date(pending.input.restoreAt).toLocaleString('zh-HK'):'直至手動恢復'}</p>:null}
        <div className="sheet-actions"><button type="button" autoFocus onClick={dismiss}>取消</button><button type="button" disabled={disabled} onClick={confirm}>確認</button></div>
      </section>
    </div>:null}
  </section>;
}
